import { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  /**
   * Test ID for testing purposes
   * @default 'error-boundary'
   */
  testId?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  retryCount: number;
}

/**
 * ErrorBoundary - Catches JavaScript errors anywhere in the child component tree
 * 
 * Implements graceful degradation and provides user-friendly error UI.
 * 
 * @example
 * ```tsx
 * <ErrorBoundary>
 *   <MyComponent />
 * </ErrorBoundary>
 * ```
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: 0,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // SEMPRE logar erros (incluindo produção)
      console.error('ErrorBoundary caught an error:', error, errorInfo);
    console.error('Error stack:', error.stack);
    console.error('Component stack:', errorInfo.componentStack);
    
    // Salvar erro no localStorage para debug posterior
    try {
      const errorLog = {
        timestamp: new Date().toISOString(),
        message: error.message,
        stack: error.stack,
        componentStack: errorInfo.componentStack,
        userAgent: navigator.userAgent,
        url: window.location.href,
      };
      
      const existingLogs = JSON.parse(localStorage.getItem('app_errors') || '[]');
      existingLogs.push(errorLog);
      // Manter apenas os últimos 10 erros
      if (existingLogs.length > 10) {
        existingLogs.shift();
      }
      localStorage.setItem('app_errors', JSON.stringify(existingLogs));
    } catch (e) {
      console.error('Failed to save error to localStorage:', e);
    }
    
    this.setState({
      error,
      errorInfo,
    });

    // Detect error type
    const errorType = this.detectErrorType(error);
    
    // Auto-retry for network errors (max 3 times)
    if (errorType === 'network' && this.state.retryCount < 3) {
      setTimeout(() => {
        this.handleRetry();
      }, 1000 * (this.state.retryCount + 1)); // Exponential backoff
    }

    // TODO: Log to error reporting service (e.g., Sentry)
  }

  /**
   * Detect error type for better handling
   */
  detectErrorType(error: Error): 'network' | 'validation' | 'permission' | 'unknown' {
    const message = error.message.toLowerCase();
    
    if (
      message.includes('network') ||
      message.includes('fetch') ||
      message.includes('connection') ||
      message.includes('timeout')
    ) {
      return 'network';
    }
    
    if (
      message.includes('validation') ||
      message.includes('invalid') ||
      message.includes('required')
    ) {
      return 'validation';
    }
    
    if (
      message.includes('permission') ||
      message.includes('unauthorized') ||
      message.includes('forbidden')
    ) {
      return 'permission';
    }
    
    return 'unknown';
  }

  handleReset = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: 0,
    });
  };

  handleRetry = (): void => {
    this.setState(prevState => ({
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: prevState.retryCount + 1,
    }));
  };

  render(): ReactNode {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Detect error type
      const errorType = this.state.error ? this.detectErrorType(this.state.error) : 'unknown';
      const isRetryable = errorType === 'network' || errorType === 'unknown';
      const canRetry = this.state.retryCount < 3;

      // Error messages by type
      const errorMessages = {
        network: {
          title: 'Erro de Conexão',
          description: 'Não foi possível conectar ao servidor. Verifique sua internet.',
        },
        validation: {
          title: 'Dados Inválidos',
          description: 'Alguns dados estão incorretos. Verifique e tente novamente.',
        },
        permission: {
          title: 'Sem Permissão',
          description: 'Você não tem permissão para acessar este recurso.',
        },
        unknown: {
          title: 'Algo deu errado',
          description: 'Ocorreu um erro inesperado. Tente novamente.',
        },
      };

      const errorMsg = errorMessages[errorType];

      // Default error UI
      return (
        <div 
          className="flex min-h-screen items-center justify-center p-4 bg-background"
          data-testid={this.props.testId || 'error-boundary'}
          role="alert"
          aria-live="assertive"
        >
          <Card className="w-full max-w-md">
            <CardHeader className="text-center">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-danger-100 dark:bg-danger-900/20">
                <AlertTriangle className="h-8 w-8 text-danger-600 dark:text-danger-400" aria-hidden="true" />
              </div>
              <CardTitle data-testid="error-boundary-title">
                {errorMsg.title}
              </CardTitle>
              <CardDescription data-testid="error-boundary-description">
                {errorMsg.description}
                {this.state.retryCount > 0 && (
                  <small className="block mt-2">
                    Tentativa {this.state.retryCount} de 3
                  </small>
                )}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {process.env.NODE_ENV === 'development' && this.state.error && (
                <details className="rounded-md bg-muted p-4">
                  <summary className="cursor-pointer mb-2">
                    <strong>Detalhes do erro (dev only)</strong>
                  </summary>
                  <pre className="overflow-auto">
                    <small><code>{this.state.error.toString()}</code></small>
                  </pre>
                  {this.state.errorInfo && (
                    <pre className="mt-2 overflow-auto">
                      <small><code>{this.state.errorInfo.componentStack}</code></small>
                    </pre>
                  )}
                </details>
              )}
              <div className="flex gap-2">
                {isRetryable && canRetry ? (
                  <Button 
                    onClick={this.handleRetry} 
                    className="flex-1"
                    data-testid="error-boundary-retry-btn"
                  >
                    Tentar Novamente ({3 - this.state.retryCount} restantes)
                  </Button>
                ) : (
                  <Button 
                    onClick={this.handleReset} 
                    className="flex-1"
                    data-testid="error-boundary-reset-btn"
                  >
                    Voltar
                  </Button>
                )}
                <Button 
                  onClick={() => window.location.reload()} 
                  variant="outline"
                  className="flex-1"
                  data-testid="error-boundary-reload-btn"
                >
                  <RefreshCw className="mr-2 h-4 w-4" aria-hidden="true" />
                  Recarregar
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}
